import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { Copy, Check, AlertCircle, Trash2 } from "lucide-react";

// ── Component ────────────────────────────────────────────────

export function RegexTester() {
    const [pattern, setPattern] = useState("");
    const [flags, setFlags] = useState("g");
    const [testString, setTestString] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        document.title = "Regex Tester — Test Regular Expressions Online — Ballah Code";
        const meta = document.querySelector('meta[name="description"]');
        if (meta) {
            meta.setAttribute("content",
                "Free online regex tester. Test regular expressions with real-time matching, highlighting, and match details. Supports JavaScript regex syntax.");
        }
    }, []);

    const results = useMemo(() => {
        if (!pattern || !testString) return { matches: [], regex: null };
        setError(null);
        try {
            const regex = new RegExp(pattern, flags);
            const matches: { text: string; index: number; groups?: Record<string, string> }[] = [];

            if (flags.includes("g")) {
                let match;
                while ((match = regex.exec(testString)) !== null) {
                    matches.push({ text: match[0], index: match.index, groups: match.groups });
                    if (match[0].length === 0) regex.lastIndex++;
                }
            } else {
                const match = regex.exec(testString);
                if (match) matches.push({ text: match[0], index: match.index, groups: match.groups });
            }

            return { matches, regex };
        } catch (e) {
            setError(e instanceof Error ? e.message : "Invalid regex");
            return { matches: [], regex: null };
        }
    }, [pattern, flags, testString]);

    // Build highlighted text
    const highlighted = useMemo(() => {
        if (!results.matches.length || !testString) return null;

        const parts: { text: string; match: boolean }[] = [];
        let lastIdx = 0;

        for (const m of results.matches) {
            if (m.index > lastIdx) {
                parts.push({ text: testString.slice(lastIdx, m.index), match: false });
            }
            parts.push({ text: m.text, match: true });
            lastIdx = m.index + m.text.length;
        }
        if (lastIdx < testString.length) {
            parts.push({ text: testString.slice(lastIdx), match: false });
        }

        return parts;
    }, [results.matches, testString]);

    const FLAG_OPTIONS = [
        { flag: "g", label: "Global", desc: "Find all matches" },
        { flag: "i", label: "Case insensitive", desc: "Ignore case" },
        { flag: "m", label: "Multiline", desc: "^ and $ match line boundaries" },
        { flag: "s", label: "Dotall", desc: ". matches newlines" },
    ];

    return (
        <div className="min-h-screen bg-background text-foreground font-sans">
            <nav className="flex items-center justify-between px-8 py-[18px]">
                <Link to="/" className="text-sm font-semibold tracking-[-0.03em] text-zinc-200 hover:text-white transition-colors">ballah code</Link>
                <div className="flex items-center gap-6 text-[13px] text-zinc-600">
                    <Link to="/tools" className="hover:text-zinc-400 transition-colors">tools</Link>
                    <Link to="/" className="hover:text-zinc-400 transition-colors">home</Link>
                </div>
            </nav>

            <main className="max-w-[1280px] mx-auto px-8 pt-12 pb-24">
                <div className="flex items-center gap-2 text-[13px] text-zinc-600 mb-8">
                    <Link to="/tools" className="hover:text-zinc-400 transition-colors">Tools</Link>
                    <span className="text-zinc-700">/</span>
                    <span className="text-zinc-400">Regex Tester</span>
                </div>

                <div className="mb-8">
                    <h1 className="text-3xl font-medium tracking-[-0.03em] mb-3">Regex Tester</h1>
                    <p className="text-zinc-500 text-[15px] leading-relaxed max-w-2xl">
                        Test regular expressions with real-time matching and highlighting. JavaScript regex syntax.
                    </p>
                </div>

                {/* Pattern input */}
                <div className="mb-4">
                    <div className="flex items-center bg-[#111113] border border-white/[0.06] rounded-xl overflow-hidden">
                        <span className="text-zinc-600 px-4 font-mono text-lg">/</span>
                        <input
                            value={pattern}
                            onChange={(e) => setPattern(e.target.value)}
                            placeholder="[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}"
                            spellCheck={false}
                            className="flex-1 bg-transparent py-3 text-[14px] text-zinc-300 font-mono outline-none placeholder:text-zinc-800"
                        />
                        <span className="text-zinc-600 font-mono text-lg">/</span>
                        <input
                            value={flags}
                            onChange={(e) => setFlags(e.target.value)}
                            className="w-12 bg-transparent py-3 text-[14px] text-violet-400 font-mono outline-none text-center"
                            placeholder="gi"
                        />
                    </div>
                </div>

                {/* Flags */}
                <div className="flex flex-wrap gap-2 mb-6">
                    {FLAG_OPTIONS.map(({ flag, label }) => (
                        <button
                            key={flag}
                            onClick={() => {
                                setFlags((f) =>
                                    f.includes(flag) ? f.replace(flag, "") : f + flag,
                                );
                            }}
                            className={`px-2.5 py-1 rounded-md text-[12px] border transition-colors ${flags.includes(flag)
                                ? "border-violet-400/30 bg-violet-400/10 text-violet-400"
                                : "border-white/[0.06] text-zinc-600 hover:text-zinc-400"
                                }`}
                        >
                            {label}
                        </button>
                    ))}
                    {(pattern || testString) && (
                        <button
                            onClick={() => { setPattern(""); setTestString(""); setError(null); }}
                            className="flex items-center gap-1 px-2.5 py-1 rounded-md text-[12px] border border-white/[0.06] text-zinc-600 hover:text-zinc-400 transition-colors ml-auto"
                        >
                            <Trash2 size={11} /> Clear
                        </button>
                    )}
                </div>

                {/* Error */}
                {error && (
                    <div className="flex items-center gap-2 text-[13px] text-red-400 mb-4 p-3 bg-red-400/[0.05] border border-red-400/10 rounded-xl">
                        <AlertCircle size={14} /> {error}
                    </div>
                )}

                {/* Test string */}
                <div className="mb-6">
                    <div className="px-4 py-2.5 bg-[#0d0d0f] border border-white/[0.06] border-b-0 rounded-t-xl flex items-center justify-between">
                        <span className="text-[11px] text-zinc-600 font-mono uppercase tracking-wider">Test String</span>
                        <span className="text-[11px] text-zinc-700">
                            {results.matches.length} match{results.matches.length !== 1 ? "es" : ""}
                        </span>
                    </div>
                    <textarea
                        value={testString}
                        onChange={(e) => setTestString(e.target.value)}
                        placeholder="Type or paste text to test against..."
                        spellCheck={false}
                        className="w-full min-h-[200px] bg-[#111113] border border-white/[0.06] rounded-b-xl p-4 text-[13px] text-zinc-300 font-mono leading-relaxed outline-none resize-y placeholder:text-zinc-800 focus:border-white/[0.1] transition-colors"
                    />
                </div>

                {/* Highlighted result */}
                {highlighted && (
                    <div className="mb-6">
                        <div className="px-4 py-2.5 bg-[#0d0d0f] border border-white/[0.06] border-b-0 rounded-t-xl">
                            <span className="text-[11px] text-zinc-600 font-mono uppercase tracking-wider">Highlighted Matches</span>
                        </div>
                        <div className="bg-[#111113] border border-white/[0.06] rounded-b-xl p-4 text-[13px] font-mono leading-relaxed whitespace-pre-wrap">
                            {highlighted.map((part, i) =>
                                part.match ? (
                                    <mark key={i} className="bg-violet-400/20 text-violet-300 rounded px-0.5">
                                        {part.text}
                                    </mark>
                                ) : (
                                    <span key={i} className="text-zinc-400">{part.text}</span>
                                ),
                            )}
                        </div>
                    </div>
                )}

                {/* Match details */}
                {results.matches.length > 0 && (
                    <div className="bg-[#111113] border border-white/[0.06] rounded-xl overflow-hidden mb-16">
                        <div className="px-4 py-2.5 border-b border-white/[0.04]">
                            <span className="text-[11px] text-zinc-600 font-mono uppercase tracking-wider">Match Details</span>
                        </div>
                        {results.matches.map((m, i) => (
                            <div key={i} className={`flex items-center justify-between px-4 py-2.5 text-[13px] ${i < results.matches.length - 1 ? "border-b border-white/[0.04]" : ""}`}>
                                <div className="flex items-center gap-4">
                                    <span className="text-zinc-700 text-[11px] font-mono w-8">#{i + 1}</span>
                                    <code className="text-violet-300">{m.text}</code>
                                </div>
                                <span className="text-zinc-600 text-[12px]">index {m.index}</span>
                            </div>
                        ))}
                    </div>
                )}

                <section className="max-w-2xl border-t border-white/[0.04] pt-12">
                    <h2 className="text-lg font-medium tracking-[-0.02em] mb-4 text-zinc-300">About regex testing</h2>
                    <div className="space-y-4 text-[14.5px] text-zinc-500 leading-[1.75]">
                        <p>Regular expressions (regex) are patterns used to match character combinations in strings. They're essential for validation, search, and text manipulation in every programming language.</p>
                        <p>This tester uses JavaScript regex syntax and supports global, case-insensitive, multiline, and dotall flags. Matches are highlighted in real-time as you type.</p>
                        <p>Built by the team behind <Link to="/" className="text-violet-400 hover:text-violet-300 transition-colors">Ballah Code</Link>.</p>
                    </div>
                    <div className="mt-8 flex flex-wrap gap-2">
                        <span className="text-[12px] text-zinc-700 mr-1 self-center">Related →</span>
                        <Link to="/tools/diff-checker" className="text-[12px] text-zinc-500 hover:text-violet-400 transition-colors px-2.5 py-1 rounded-md border border-white/[0.06] hover:border-violet-400/30">Diff Checker</Link>
                        <Link to="/tools/json-formatter" className="text-[12px] text-zinc-500 hover:text-violet-400 transition-colors px-2.5 py-1 rounded-md border border-white/[0.06] hover:border-violet-400/30">JSON Formatter</Link>
                        <Link to="/tools/url-encode-decode" className="text-[12px] text-zinc-500 hover:text-violet-400 transition-colors px-2.5 py-1 rounded-md border border-white/[0.06] hover:border-violet-400/30">URL Encode / Decode</Link>
                    </div>
                </section>
            </main>

            <footer className="border-t border-white/[0.04] py-5 px-8 flex justify-between max-w-[1280px] mx-auto text-xs text-zinc-800">
                <Link to="/" className="hover:text-zinc-600 transition-colors">ballah code</Link>
                <Link to="/tools" className="hover:text-zinc-600 transition-colors">tools</Link>
                <span>ballah tech</span>
            </footer>
        </div>
    );
}
